/*
 * Using Modules
 * =============
 * 1. Import modules
 * -----------------
 * import Modal from './modules/navigation';
 *
 * 2. Assign modules to the Freshworks global object for exposure
 * to the window scope.
 * --------------------------------------------------------------
 * Freshworks.navigation = navigation;
 */

// Add Babel Polyfills to be able to use instance methods
// like [].forEach() etc.
import 'babel-polyfill';
import './modules/polyfills';
/*
 * NOTE: Import Bootstrap modules using the format below
 * import 'Bootstrap/carousel';
 */
import 'Bootstrap/transition'; // carousel dependency
import 'Bootstrap/carousel';
import 'Bootstrap/tooltip';
import 'Bootstrap/popover';
import 'Bootstrap/tab';
import 'Bootstrap/modal';

// Import other vendor modules
// webpack.ProvidePlugin is used to import jquery(3.2.1) across all modules
import 'jquery-validation';
import './vendor/session';

// Import other necessary modules
import Utils from './modules/utils';
import { navigation } from './modules/navigation';
import { pricingCompare } from './modules/compare-pricing';
import { pricingFeatureComparison } from './modules/pricing-feature-comparison';
import comparisonTableV2 from './modules/plans-comparison-table-v2';
import { footer } from './modules/footer';
import { customPopover } from './modules/custom-popover';
import { pricingtable } from './modules/pricing-table';
import { pricingOffertable } from './modules/pricing-offer-table';
import Submit from './modules/form-submit-handlers';
import { modalbox } from './modules/modalbox';
import { videoWidget } from './modules/video-widget';
import { comparisonScrollActions } from './modules/comparison';
import { tableSticky } from './modules/table';
import { emailOnlySignup } from './modules/email-only-signup';
import { accordion } from './modules/accordion';
import { accordionData } from './modules/accordion-data';
import pricing from './modules/pricing-data';
import { mosaicGrid } from './modules/mosaic-grid';
import { customCarousel } from './modules/custom-carousel';
import { thankyou } from './modules/thankyou';
import { socialAuthentication } from './modules/signup-social';
import { lazyLoader } from './modules/lazy-loader';
import Loaders from './modules/plugin-loaders';
import calendly from './modules/calendly';
import fwcrmSignupType from './modules/fwcrm-sign-up-type';
import formSsoSignup from './modules/form-sso-signup';
import formMarketplaceIntegration from './modules/form-marketplace-integration';
import { customDropdown } from './modules/custom-dropdown';
import { callRates } from './modules/call-rates';
import { callRatesBrowser } from './modules/call-rates-with-browser';
import { scrollTo } from './modules/scroll-to';
import { getGAId } from './modules/cookie-bind';
import { roiCalculator } from './modules/roi-calculator';
import { enableGDPR } from './modules/enable-gdpr-for-eu';
import { showButton, loadMore } from './modules/webinar-show-more-button';
import { countryCodeSelector } from './modules/country-code-selector';
import legal from './modules/legal';
import EqualHeight from './modules/equal-height';
import { prefetch } from './modules/prefetch-url';
import { resourceCard, setNormalizedHeightOnResourceBox } from './modules/resource-card';
import RestrictedDomains from './modules/restricted-domains';
import { webVisits } from './modules/web-visits';
import { pricingDropdown } from './modules/pricing-product-dropdown';
import { trapModalFocus } from './modules/modal-focus';
import { generateSchemaMarkup } from './modules/schema-markup';
import { loadIframeInit } from './modules/lazy-load-iframe';
import { fchatCustomIconInit } from './modules/fchat-custom-icon';
import { affiliateMarketingScriptInit } from './modules/affiliate-marketing-handlers/index';
import hawkEye from './modules/hawk-eye-pulse';
import { setupGlobalErrorHandlers } from './modules/client-side-error-handler';

/* Globals */
window.breakpoints = Object.freeze({
  sm: 480,
  md: 768,
  lg: 960
});

setupGlobalErrorHandlers();

/*
 * Modules to be exposed on the website should be added to the
 * global Freshworks object.
 */
var Freshworks = {
  $,
  jQuery,
  Utils,
  navigation,
  pricingCompare,
  pricingFeatureComparison,
  footer,
  customPopover,
  pricingtable,
  pricingOffertable,
  pricing,
  Submit,
  modalbox,
  socialAuthentication,
  videoWidget,
  comparisonScrollActions,
  tableSticky,
  emailOnlySignup,
  accordion,
  accordionData,
  customCarousel,
  thankyou,
  lazyLoader,
  customDropdown,
  pricingDropdown,
  callRates,
  callRatesBrowser,
  scrollTo,
  getGAId,
  roiCalculator,
  showButton,
  loadMore,
  countryCodeSelector,
  legal,
  EqualHeight,
  prefetch,
  calendly,
  fwcrmSignupType,
  formSsoSignup,
  formMarketplaceIntegration,
  setNormalizedHeightOnResourceBox,
  crossDomainConnect: {
    storage: {
      cookies: [],
      localStorage: ['latest_page_visits']
    },
    urls: {}
  },
  trapModalFocus
};

/*
 * `onLoad` method (that replaces the dummy onLoad that just stores the arguments for every call)
 * Usage 1) FW.onLoad(fn) - call fn(FW) after this script is loaded
 * Usage 2) FW.onLoad(moduleName, fn) - call fn(module, FW) after both are loaded
 * Usage 3) FW.onLoad(module1, module2, ..., module-n, fn) - call fn(module1, module2, ..., module-n, FW) after all are loaded
 */
Freshworks.onLoad = function () {
  try {
    let callback = arguments[arguments.length - 1];
    callback = (typeof callback === 'function') ? callback : () => void 0;
    const modules = [...arguments].slice(0, -1);
    if (modules.length) {
      Utils.SiteUtilities.onModuleLoad(modules)
        .then(mods => callback(...mods, Freshworks))
        .catch(err => Utils.SiteUtilities.log(
          'Requested Module(s) failed to load ' + err, 'error',
          `Module(s) - ${modules}\n${err.stack}`, 0, ''
        ));
    } else {
      callback(Freshworks);
    }
  } catch (e) {
    Utils.SiteUtilities.log(
      e.message, 'error',
      'FW.onLoad - ' + e.stack, 2, e
    );
  }
};

/*
 * NOTE: DO NOT add modules to the Freshworks object beyond this
 * this point. The object will be frozen before it's added to the
 * window's scope to prevent tampering of modules. Add all modules
 * before the Object.freeze(Freshworks) call.
 */
Object.freeze(Freshworks);

const cbQueue = window.FW.cbQueue || [];
window.FW = Freshworks;
cbQueue.map(args => Freshworks.onLoad(...args));

(function init () {
  Utils.SiteUtilities.setInitialSignupPerformanceLog();
  Loaders.init();
  Utils.init();
  RestrictedDomains.init();
  enableGDPR(Freshworks);
  affiliateMarketingScriptInit();
  lazyLoader();
  comparisonTableV2.init();
  window.addEventListener('OneTrustGroupsUpdated', () => Utils.SiteUtilities.setInitialTargetingCookies());
  // formUtilities.UIAnimations();
  // formUtilities.FieldValidation();
  emailOnlySignup(Freshworks);
  mosaicGrid();
  scrollTo();
  accordion();
  accordionData();
  videoWidget();
  resourceCard();
  customCarousel();
  getGAId();
  prefetch();
  webVisits();
  generateSchemaMarkup();
  loadIframeInit();
  fchatCustomIconInit();
  hawkEye.init();
}());

/*
 * The inlineLoader dispatches the custom event 'jQueryLoaded'
 * which triggers all inline scripts to run safely. This is done
 * to prevent breakages in those scripts which use jQuery (jQuery
 * is loaded at the bottom of the page instead of at the top).
 *
 * NOTE: This function should ALWAYS be called at the absolute end
 * of the freshworks.js file to ensure that all modules are loaded
 * and ready to use when the inline calls to the modules get fired.
 */
// Freshworks.loaders.inlineLoader();
